<template>
    <form
        ref="dropzoneElement"
        class="dropzone"
    />
</template>

<script lang="ts">

import {computed, defineComponent, ref, watch} from "vue";
import Dropzone, {DropzoneFile, DropzoneOptions} from "dropzone";
import {useAunoaI18n} from "../utils/useAunoaI18n";
import {useUrlTools} from "../utils/useUrlTools";
import {useToasts} from "../utils/useToasts";

export default defineComponent({
    name: "AunoaDropzone",
    props: {
        url: {
            type: String,
            default: "/target"
        },
        acceptedFiles: {
            type: String
        },
        method: {
            type: String,
            default: "post"
        },
        timeout: {
            type: Number,
            default: null
        },
        parallelUploads: {
            type: Number,
            default: 1
        },
        uploadMultiple: {
            type: Boolean,
            default: false
        },
        maxFiles: {
            type: Number,
            default: null
        }
    },
    emits: ["select"],
    setup: (props, {emit}) => {

        const errorMessage = "One or more errors occurred while uploading files. Please check the files inside the drop zone.";
        const {addErrorToast} = useToasts()
      
        const dropzoneElement = ref<HTMLFormElement>();

        const {t} = useAunoaI18n()
        
        const {ensureUrl, createAuthorizationHeader} = useUrlTools()

        const mergeOptions = (options: DropzoneOptions) => {
            options.headers = createAuthorizationHeader(props.url);
            options.url = ensureUrl(props.url);
            options.acceptedFiles = props.acceptedFiles;
            options.method = props.method;
            options.timeout = props.timeout;
            options.parallelUploads = props.parallelUploads;
            options.uploadMultiple = props.uploadMultiple;
            options.maxFiles = props.maxFiles;
            options.addRemoveLinks = true;
            return options;
        }

        function init(this: Dropzone) {

            let uploadedFiles: DropzoneFile[];
            let temporaryFileKeys: string[];
          
            this.on("addedfile", () => {
              if (uploadedFiles != undefined){
                uploadedFiles.forEach((f) => {
                  this.removeFile(f);
                });
              }
              
              mergeOptions(this.options);
              emit("select", uploadedFiles, temporaryFileKeys);
            });

          this.on("maxfilesexceeded", () => {
            addErrorToast("Maximum number of files exceeded. Not all files were uploaded.");
          });
          
          this.on("canceled", () => {
              if (uploadedFiles != undefined){
                uploadedFiles.forEach((f) => {
                  this.removeFile(f);
                });
              }
              
            });
            
            this.on("canceledmultiple", () => {
              if (uploadedFiles != undefined){
                uploadedFiles.forEach((f) => {
                  this.removeFile(f);
                });
              }
            });
          
            this.on("removedfile", (file:DropzoneFile) => {
              if (uploadedFiles != undefined){
                const index = uploadedFiles.indexOf(file);
                if (index !== -1) {
                  uploadedFiles.splice(index, 1);
                }
              }

              if (temporaryFileKeys == undefined)
              {
                emit("select", uploadedFiles, temporaryFileKeys);
                return;
              }
              const fileNameWithoutExtension = file.name.substring(0, file.name.lastIndexOf('.'));
              const temporaryFileKey = temporaryFileKeys.find(k => k.includes(fileNameWithoutExtension));
              if (temporaryFileKey == null)
              {
                emit("select", uploadedFiles, temporaryFileKeys);
                return;
              }

              const index = temporaryFileKeys.indexOf(temporaryFileKey);
              if (index !== -1) {
                temporaryFileKeys.splice(index,1);
              }
              
              emit("select", uploadedFiles, temporaryFileKeys);
            });

            if (this.options.uploadMultiple) {
                this.on("successmultiple", (files: DropzoneFile[], result: any) => {
                  uploadedFiles = files;
                  temporaryFileKeys = result;
                  emit("select", files, temporaryFileKeys);
                });

            } else {
                this.on("success", (file: DropzoneFile, result: any) => {
                  uploadedFiles = [file];
                  temporaryFileKeys = result;
                  emit("select", file, temporaryFileKeys);
                });
            }

          if (this.options.uploadMultiple) {
            this.on("errormultiple", () => {
              addErrorToast(errorMessage);
            });

          } else {
            this.on("error", () => {
              addErrorToast(errorMessage);
            });
          }
        }
      
      const dropzoneOptions = computed<DropzoneOptions>(() => ({
        ...mergeOptions({}),
        dictDefaultMessage: t("Aunoa.Dropzone.DefaultMessage", {maxFiles: props.maxFiles}),
        dictFallbackMessage: t("Aunoa.Dropzone.FallbackMessage"),
        dictFallbackText: t("Aunoa.Dropzone.FallbackText"),
        dictFileTooBig: t("Aunoa.Dropzone.FileTooBig"),
        dictInvalidFileType: t("Aunoa.Dropzone.InvalidFileType"),
        dictResponseError: t("Aunoa.Dropzone.ResponseError"),
        dictCancelUpload: t("Aunoa.Dropzone.CancelUpload"),
        dictCancelUploadConfirmation: t("Aunoa.Dropzone.CancelUploadConfirmation"),
        dictRemoveFile: t("Aunoa.Dropzone.RemoveFile"),
        dictMaxFilesExceeded: t("Aunoa.Dropzone.MaxFilesExceeded"),
        init
      }));
      

        let dropzone: Dropzone | undefined;
        watch([dropzoneElement, dropzoneOptions], ([element, options], [oldElement]) => {
            if (oldElement) {
                if (dropzone) {
                    dropzone.destroy();
                    dropzone = undefined;
                }
                oldElement.innerHTML = "";
            }
            if (element) {
                // console.log(options);
                dropzone = new Dropzone(element, options);
            }
        });

        return {
            dropzoneElement,
            dropzoneOptions
        }

    }
})

</script>
